/** Предварительные группы, трассы и связи освещения до применения к проекту. */
import { useState } from "react";
import { CablePurpose, cableItemById, ElectricalId, switchKeyCount } from "@/shared/catalog";
import { CatalogSectionKind } from "@/shared/plan";
import { layersChanged, useProjectStore } from "@/shared/store";
import { remove } from "@/shared/store/actions";
import { useFixturesStore, useWiringStore } from "@/shared/store/layers";
import { UIKit } from "@/shared/ui";
import {
	addLightingLink,
	autoCableTurn,
	autoEndpointLabels,
	autoWiringWalls,
	buildAutoWiring,
	cableEndCatalogItem,
	matchingAutoGroup,
	overlappingRouteLength,
	preserveCableLighting,
	routeWallCrossings,
	routesNear,
	unusedWiringBoxes,
	unusedWiringGroups,
} from "@/widgets/electrical";
import { cableRoute } from "@/widgets/plan";
import { SelectionKind, useEditorStore } from "@/widgets/editor";

const toggle = (set: Set<string>, value: string, checked: boolean) => {
	const next = new Set(set);
	if (checked) next.add(value);
	else next.delete(value);
	return next;
};

export const WiringAutoDialog = ({ onClose }: { onClose: () => void }) => {
	const layers = useProjectStore((state) => state.project.layers);
	const style = useEditorStore((state) => state.lightingConnectionStyle);
	const [snapshot] = useState(() => layers);
	const endpointLabels = autoEndpointLabels(
		[
			...Object.values(snapshot.electrical.fixtures),
			...Object.values(snapshot.electrical.items),
		].map((item) => ({
			id: item.id,
			name: cableEndCatalogItem(snapshot.electrical, item.id)?.name ?? "Прибор",
		})),
	);
	const [initialDraft] = useState(() => buildAutoWiring(layers, style));
	const [rebuild, setRebuild] = useState(false);
	const [removeUnusedBoxes, setRemoveUnusedBoxes] = useState(true);
	const [removeUnusedGroups, setRemoveUnusedGroups] = useState(true);
	const [selectedSources, setSelectedSources] = useState<Record<string, string>>({});
	const [excludedTargets, setExcludedTargets] = useState<Set<string>>(() => new Set());
	const [selectedSwitches, setSelectedSwitches] = useState<Record<string, string>>({});
	const [turns, setTurns] = useState<Record<string, "horizontal" | "vertical">>({});
	const planned = buildAutoWiring(snapshot, style, selectedSources, excludedTargets, rebuild);
	const draft = {
		...planned,
		groups: planned.groups.map((group) => ({
			...group,
			cables: group.cables.map((cable) => {
				const turn = turns[`${group.id}:${cable.from}:${cable.to}`];
				return turn ? autoCableTurn(cable, turn) : cable;
			}),
		})),
	};
	const [selectedGroups, setSelectedGroups] = useState(
		() => new Set(initialDraft.groups.map((group) => group.id)),
	);
	const [selectedLinks, setSelectedLinks] = useState(
		() => new Set(initialDraft.links.map((link) => link.to)),
	);
	const [keys, setKeys] = useState<Record<string, 1 | 2 | 3>>({});
	const [error, setError] = useState("");
	const groups = draft.groups.filter((group) => selectedGroups.has(group.id));
	const unresolved = groups.some((group) => !group.sourceId || !group.cables.length);
	const nodes = Object.values(snapshot.walls.nodes);
	const minX = Math.min(0, ...nodes.map((node) => node.x));
	const minY = Math.min(0, ...nodes.map((node) => node.y));
	const width = Math.max(1000, ...nodes.map((node) => node.x - minX));
	const height = Math.max(1000, ...nodes.map((node) => node.y - minY));
	const wallSegments = autoWiringWalls(snapshot.walls);
	const crossings = groups.flatMap((group) =>
		group.cables.flatMap((cable) => routeWallCrossings(cable.route, wallSegments)),
	);
	const lines = groups.flatMap((group) =>
		group.cables.map((cable) => ({
			route: cable.route,
			low: group.purpose === "network" || group.purpose === "tv",
		})),
	);
	const existingLines = Object.values(rebuild ? {} : snapshot.wiring.items).flatMap((cable) => {
		const route = cableRoute(snapshot.walls, snapshot.electrical, cable);
		const material = cable.material ? cableItemById(cable.material) : null;
		return route && material
			? [
					{
						route,
						low:
							material.purpose === CablePurpose.LowVoltage || material.purpose === CablePurpose.Tv,
					},
				]
			: [];
	});
	const uncheckedExisting =
		!rebuild &&
		Object.values(snapshot.wiring.items).some(
			(cable) =>
				!cable.material ||
				!cableItemById(cable.material) ||
				!cableRoute(snapshot.walls, snapshot.electrical, cable),
		);
	const near = lines.some((line, index) =>
		[...lines.slice(index + 1), ...existingLines].some(
			(other) => line.low !== other.low && routesNear(line.route, other.route),
		),
	);
	const overlap = lines.some((line, index) =>
		[...lines.slice(index + 1), ...existingLines].some(
			(other) => overlappingRouteLength(line.route, other.route) > 100,
		),
	);
	const apply = () => {
		if (unresolved) return;
		const current = useProjectStore.getState().project.layers;
		if (layersChanged(snapshot, current)) {
			setError("План изменился. Закройте окно и рассчитайте предложения заново.");
			return;
		}
		let sourceFailed = false;
		useProjectStore.getState().recordEdit(() => {
			const draftSource = draft.sources.find(
				(source) => source.placement && groups.some((group) => group.sourceId === source.id),
			);
			const sourcePlacement = draftSource?.placement;
			const newSourceId = sourcePlacement
				? useFixturesStore
						.getState()
						.addFixture(
							sourcePlacement.wallId,
							{ catalog: CatalogSectionKind.Electrical, item: ElectricalId.LowVoltagePanel },
							sourcePlacement.offset,
							sourcePlacement.elevation,
							sourcePlacement.side,
						)
				: null;
			if (draftSource && !newSourceId) {
				sourceFailed = true;
				return;
			}
			if (rebuild) {
				preserveCableLighting(snapshot.electrical, snapshot.wiring.items, style);
				useWiringStore.getState().restoreCables({});
			}
			const usedGroups = new Set<string>();
			for (const group of groups) {
				let boxId: string | null = null;
				if (group.box) {
					boxId =
						group.box.existingId ??
						(group.box.ceiling
							? useFixturesStore.getState().addCeilingItem(
									{
										catalog: CatalogSectionKind.Electrical,
										item: ElectricalId.CeilingJunctionBox,
									},
									group.box.point.x,
									group.box.point.y,
								)
							: useFixturesStore
									.getState()
									.addFixture(
										group.box.wallId,
										{ catalog: CatalogSectionKind.Electrical, item: ElectricalId.JunctionBox },
										group.box.offset,
										group.box.elevation,
										group.box.side,
									));
					if (!boxId) continue;
					if (group.box.ceiling && !group.box.existingId)
						useFixturesStore.getState().updateCeilingItem(boxId, { autoWiring: true });
				}
				const wiring = useWiringStore.getState();
				const groupInfo = {
					name: group.name,
					purpose: group.purpose,
					panelId: group.sourceId === draftSource?.id ? newSourceId! : group.sourceId,
				};
				const groupId =
					matchingAutoGroup(wiring.groups, groupInfo, usedGroups) ?? wiring.addGroup(groupInfo);
				usedGroups.add(groupId);
				for (const cable of group.cables)
					wiring.addCable(
						cable.from === group.boxId
							? boxId!
							: cable.from === draftSource?.id
								? newSourceId!
								: cable.from,
						cable.to === group.boxId
							? boxId!
							: cable.to === draftSource?.id
								? newSourceId!
								: cable.to,
						cable.points,
						group.material,
						groupId,
					);
			}
			for (const link of draft.links.filter((candidate) => selectedLinks.has(candidate.to))) {
				const wiring = useWiringStore.getState();
				const id = addLightingLink(
					snapshot.electrical,
					snapshot.wiring.items,
					selectedSwitches[link.to] ?? link.from,
					link.to,
					link.style,
				);
				if (id && keys[link.to]) wiring.updateLightingConnection(id, { key: keys[link.to] });
			}
			if (rebuild) {
				if (removeUnusedGroups) {
					const wiring = useWiringStore.getState();
					for (const id of unusedWiringGroups(wiring.groups, wiring.items)) wiring.removeGroup(id);
				}
				const unused = unusedWiringBoxes(
					useProjectStore.getState().project.layers,
					removeUnusedBoxes,
				);
				for (const id of unused.fixtures) remove({ kind: SelectionKind.Fixture, id });
				for (const id of unused.ceilingItems) remove({ kind: SelectionKind.Light, id });
			}
		});
		if (sourceFailed) {
			setError("Не удалось разместить слаботочный узел. Пересчитайте черновик.");
			return;
		}
		onClose();
	};
	return (
		<UIKit.Modal ariaLabel="Автоматическая проводка" onClose={onClose} className="p-4 md:max-w-3xl">
			<div className="flex flex-col gap-3">
				<UIKit.Text className="text-lg font-semibold">Автоматическая проводка</UIKit.Text>
				<UIKit.Checkbox
					checked={rebuild}
					label="Перестроить все кабели с нуля"
					onChange={(checked) => {
						const next = buildAutoWiring(snapshot, style, {}, new Set(), checked);
						setRebuild(checked);
						setSelectedSources({});
						setExcludedTargets(new Set());
						setTurns({});
						setSelectedGroups(new Set(next.groups.map((group) => group.id)));
						setSelectedLinks(new Set(next.links.map((link) => link.to)));
					}}
				/>
				{rebuild && (
					<>
						<UIKit.Text className="text-sm text-amber-700">
							При применении все существующие кабели будут удалены, в том числе не включённые в
							новые группы. Группы и связи света сохранятся. Действие можно отменить.
						</UIKit.Text>
						<UIKit.Checkbox
							checked={removeUnusedBoxes}
							onChange={setRemoveUnusedBoxes}
							label="Удалить неподключённые распредкоробки без комментариев"
						/>
						<UIKit.Checkbox
							checked={removeUnusedGroups}
							onChange={setRemoveUnusedGroups}
							label="Удалить пустые группы"
						/>
					</>
				)}
				{draft.sources.some(
					(source) => source.placement && groups.some((group) => group.sourceId === source.id),
				) && (
					<UIKit.Text className="text-sm text-amber-700">
						Предлагается новый слаботочный узел для LAN и ТВ. Проверьте его место и наличие питания
						для оборудования.
					</UIKit.Text>
				)}
				<UIKit.Text className="text-sm text-amber-700">
					Это эскиз, не проект электроснабжения. Проверьте трассы, места коробок, сечения и защиту
					со специалистом. Марки и сечения не рассчитаны; разделение по высоте не учитывается.
					Проход над дверью не означает разрешения сверлить перемычку.
				</UIKit.Text>
				{nodes.length > 0 && (
					<svg
						aria-label="Предпросмотр трасс"
						role="img"
						viewBox={`${minX - 200} ${minY - 200} ${width + 400} ${height + 400}`}
						className="h-48 w-full rounded-md border border-border bg-bg"
					>
						{Object.values(snapshot.walls.walls).map((wall) => {
							const start = snapshot.walls.nodes[wall.a];
							const end = snapshot.walls.nodes[wall.b];
							return start && end ? (
								<line
									key={wall.id}
									x1={start.x}
									y1={start.y}
									x2={end.x}
									y2={end.y}
									stroke="#89939b"
									strokeWidth={Math.max(wall.thickness, 25)}
								/>
							) : null;
						})}
						{groups.flatMap((group) =>
							group.cables.map((cable, index) => (
								<polyline
									key={`${group.id}-${index}`}
									points={cable.route.map((point) => `${point.x},${point.y}`).join(" ")}
									fill="none"
									stroke={cableItemById(group.material)?.color ?? "#d97706"}
									strokeWidth="35"
								/>
							)),
						)}
						{groups
							.filter((group) => group.box && group.cables.length)
							.map((group) => (
								<circle
									key={group.id}
									cx={group.box!.point.x}
									cy={group.box!.point.y}
									r="75"
									fill="#334155"
								/>
							))}
						{draft.sources
							.filter(
								(source) =>
									source.placement && groups.some((group) => group.sourceId === source.id),
							)
							.map((source) => (
								<rect
									key={source.id}
									x={source.point.x - 75}
									y={source.point.y - 75}
									width="150"
									height="150"
									fill="#7c3aed"
								/>
							))}
						{crossings.map((point, index) => (
							<circle key={index} cx={point.x} cy={point.y} r="55" fill="#dc2626" />
						))}
					</svg>
				)}
				{crossings.length > 0 && (
					<UIKit.Text className="text-sm text-amber-700">
						На плане найдено пересечений сплошных стен: {crossings.length}. Красные точки
						приблизительные; проверьте возможность и способ прохода. Толщина стен и высоты не
						учитываются.
					</UIKit.Text>
				)}
				{near && (
					<UIKit.Text className="text-sm text-amber-700">
						Силовые и слаботочные трассы на плане близки или пересекаются. Проверьте разделение.
					</UIKit.Text>
				)}
				{overlap && (
					<UIKit.Text className="text-sm text-amber-700">
						Некоторые кабели совпадают на плане. Проверьте и разнесите их трассы.
					</UIKit.Text>
				)}
				{uncheckedExisting && (
					<UIKit.Text className="text-sm text-amber-700">
						Некоторые существующие кабели не проверены на близость: не указана марка или недоступна
						трасса.
					</UIKit.Text>
				)}
				{draft.warnings.map((warning, index) => (
					<UIKit.Text key={index} className="text-xs text-amber-700">
						{warning}
					</UIKit.Text>
				))}
				<UIKit.Text className="text-sm font-semibold">Группы и трассы</UIKit.Text>
				{draft.groups.length === 0 && (
					<UIKit.Text className="text-sm">Нет предложений по кабелям.</UIKit.Text>
				)}
				{draft.groups.map((group) => (
					<div key={group.id} className="border-t border-border pt-2">
						<UIKit.Checkbox
							checked={selectedGroups.has(group.id)}
							onChange={(checked) => setSelectedGroups(toggle(selectedGroups, group.id, checked))}
							label={group.name}
						/>
						<UIKit.Text className="pl-5 text-xs text-ink">
							{cableItemById(group.material)?.name ?? group.material} · линий: {group.cables.length}{" "}
							· коробок: {group.box && group.cables.length ? 1 : 0}
						</UIKit.Text>
						{draft.sources.filter((source) => source.kind === group.sourceKind).length > 1 && (
							<UIKit.Select
								label="Источник"
								value={group.sourceId}
								options={[
									{ value: "", label: "Выберите источник", disabled: true },
									...draft.sources
										.filter((source) => source.kind === group.sourceKind)
										.map((source) => ({
											value: source.id,
											label: source.placement
												? "Новый слаботочный узел"
												: (endpointLabels.get(source.id) ?? "Щиток"),
										})),
								]}
								onChange={(source) =>
									setSelectedSources({ ...selectedSources, [group.id]: source })
								}
							/>
						)}
						<div className="flex flex-col gap-1 pt-2 pl-5">
							{group.targets.map((target) => (
								<UIKit.Checkbox
									key={target.id}
									checked={!excludedTargets.has(target.id)}
									onChange={(checked) =>
										setExcludedTargets(toggle(excludedTargets, target.id, !checked))
									}
									label={endpointLabels.get(target.id) ?? "Прибор"}
								/>
							))}
						</div>
						{group.cables
							.filter(
								(cable) =>
									cable.route[0].x !== cable.route.at(-1)!.x &&
									cable.route[0].y !== cable.route.at(-1)!.y,
							)
							.map((cable) => {
								const id = `${group.id}:${cable.from}:${cable.to}`;
								const endName = (end: string) =>
									end === group.boxId
										? "Коробка"
										: draft.sources.some((source) => source.id === end && source.placement)
											? "Новый слаботочный узел"
											: (endpointLabels.get(end) ?? "Прибор");
								return (
									<UIKit.Select
										key={id}
										label={`${endName(cable.from)} → ${endName(cable.to)}`}
										value={turns[id] ?? "auto"}
										options={[
											{ value: "auto", label: "Автоматически" },
											{ value: "horizontal", label: "Сначала горизонтально" },
											{ value: "vertical", label: "Сначала вертикально" },
										]}
										onChange={(value) => {
											const next = { ...turns };
											if (value === "auto") delete next[id];
											else next[id] = value;
											setTurns(next);
										}}
									/>
								);
							})}
					</div>
				))}
				<UIKit.Text className="text-sm font-semibold">Связи выключателей и светильников</UIKit.Text>
				{draft.links.length === 0 && <UIKit.Text className="text-sm">Нет новых связей.</UIKit.Text>}
				{draft.links.map((link) => (
					<div
						key={link.to}
						className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2"
					>
						<UIKit.Checkbox
							checked={selectedLinks.has(link.to)}
							onChange={(checked) => setSelectedLinks(toggle(selectedLinks, link.to, checked))}
							label={`${link.roomName}: ${endpointLabels.get(link.to) ?? link.lightName}`}
						/>
						<UIKit.Select
							label="Выключатель"
							value={selectedSwitches[link.to] ?? link.from}
							options={link.switches.map((item) => ({
								value: item.id,
								label: endpointLabels.get(item.id) ?? item.name,
							}))}
							onChange={(value) => {
								setSelectedSwitches({ ...selectedSwitches, [link.to]: value });
								setKeys({ ...keys, [link.to]: 1 });
							}}
						/>
						{switchKeyCount(
							snapshot.electrical.fixtures[selectedSwitches[link.to] ?? link.from]?.ref.item ?? "",
						) > 1 && (
							<UIKit.Select
								label="Клавиша"
								value={keys[link.to] ?? 1}
								options={([1, 2, 3] as const)
									.filter(
										(key) =>
											key <=
											switchKeyCount(
												snapshot.electrical.fixtures[selectedSwitches[link.to] ?? link.from]?.ref
													.item ?? "",
											),
									)
									.map((key) => ({ value: key, label: `Клавиша ${key}` }))}
								onChange={(key) => setKeys({ ...keys, [link.to]: key })}
							/>
						)}
					</div>
				))}
				{unresolved && (
					<UIKit.Text className="text-sm text-amber-700">
						Выберите источник и хотя бы один прибор для каждой включённой группы.
					</UIKit.Text>
				)}
				{error && <UIKit.Text className="text-sm text-danger">{error}</UIKit.Text>}
				<div className="flex justify-end gap-2 border-t border-border pt-3">
					<UIKit.Button type="button" className="px-3 py-2 text-sm" onClick={onClose}>
						Отмена
					</UIKit.Button>
					<UIKit.Button
						type="button"
						className="rounded-md bg-accent px-3 py-2 text-sm text-white"
						disabled={unresolved || (!selectedGroups.size && (rebuild || !selectedLinks.size))}
						onClick={apply}
					>
						{rebuild ? "Удалить старые кабели и построить новые" : "Применить выбранное"}
					</UIKit.Button>
				</div>
			</div>
		</UIKit.Modal>
	);
};
